// Pure tz-aware date helpers used by accounting. Intl-based; no new dep.

export type AccountingTz = 'utc' | 'local' | (string & {})

const resolvedZones = new Map<AccountingTz, string>()

/** Legacy zone names and zones unsupported by the browser resolve to UTC. */
export const resolveAccountingTimezone = (tz: AccountingTz): string => {
  const cached = resolvedZones.get(tz)
  if (cached) return cached
  const zone =
    tz === 'utc'
      ? 'UTC'
      : tz === 'local'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        : tz
  try {
    const resolved = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
    }).resolvedOptions().timeZone
    resolvedZones.set(tz, resolved)
    return resolved
  } catch (error) {
    if (error instanceof RangeError) {
      resolvedZones.set(tz, 'UTC')
      return 'UTC'
    }
    throw error
  }
}

const wallFormatters = new Map<string, Intl.DateTimeFormat>()
const abbreviationFormatters = new Map<string, Intl.DateTimeFormat>()

const getWallFormatter = (timezone: string) => {
  const cached = wallFormatters.get(timezone)
  if (cached) return cached
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
  wallFormatters.set(timezone, formatter)
  return formatter
}

const orgParts = (date: Date, timezone: string) => {
  const parts = getWallFormatter(timezone).formatToParts(date)
  const part = (type: string) =>
    Number(parts.find((p) => p.type === type)!.value)
  return {
    year: part('year'),
    month: part('month'),
    day: part('day'),
    hour: part('hour'),
    minute: part('minute'),
    second: part('second'),
  }
}

type OrgWallTime = ReturnType<typeof orgParts>

/** Resolves a wall clock time through the zone's offset, including DST. */
const orgInstant = (wall: OrgWallTime, timezone: string): Date => {
  const target = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
  )
  const adjust = (instant: number) => {
    const rendered = orgParts(new Date(instant), timezone)
    return (
      instant +
      target -
      Date.UTC(
        rendered.year,
        rendered.month - 1,
        rendered.day,
        rendered.hour,
        rendered.minute,
        rendered.second,
      )
    )
  }
  const instant = adjust(adjust(adjust(target)))
  const rendered = orgParts(new Date(instant), timezone)
  const renderedWallTime = Date.UTC(
    rendered.year,
    rendered.month - 1,
    rendered.day,
    rendered.hour,
    rendered.minute,
    rendered.second,
  )
  // If midnight falls in a DST gap, the offset calculation can oscillate
  // across the gap. Use the first available wall time after it.
  return new Date(renderedWallTime < target ? adjust(instant) : instant)
}

const shiftOrgCalendar = (
  date: Date,
  timezone: string,
  { months, days }: { months: number; days: number },
): Date => {
  const wall = orgParts(date, timezone)
  const lastDay = new Date(
    Date.UTC(wall.year, wall.month + months, 0),
  ).getUTCDate()
  const shifted = new Date(
    Date.UTC(
      wall.year,
      wall.month - 1 + months,
      Math.min(wall.day, lastDay) + days,
      wall.hour,
      wall.minute,
      wall.second,
    ),
  )
  const instant = orgInstant(
    {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth() + 1,
      day: shifted.getUTCDate(),
      hour: shifted.getUTCHours(),
      minute: shifted.getUTCMinutes(),
      second: shifted.getUTCSeconds(),
    },
    timezone,
  )
  return new Date(instant.getTime() + date.getUTCMilliseconds())
}

export const getTzAbbr = (tz: AccountingTz, at: Date = new Date()): string => {
  const zone = resolveAccountingTimezone(tz)
  let formatter = abbreviationFormatters.get(zone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'short',
    })
    abbreviationFormatters.set(zone, formatter)
  }
  const parts = formatter.formatToParts(at)
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? 'UTC'
}

export const startOfDay = (d: Date, tz: AccountingTz): Date =>
  parseYmdInTz(formatYmdInTz(d, tz), tz)

export const endOfDay = (d: Date, tz: AccountingTz): Date =>
  new Date(startOfDay(addDays(startOfDay(d, tz), 1, tz), tz).getTime() - 1)

// ISO week: Monday as first day.
export const startOfWeek = (d: Date, tz: AccountingTz): Date => {
  const sd = startOfDay(d, tz)
  const dow = new Date(`${formatYmdInTz(sd, tz)}T00:00:00Z`).getUTCDay()
  return addDays(sd, -((dow + 6) % 7), tz)
}

export const endOfWeek = (d: Date, tz: AccountingTz): Date =>
  new Date(startOfDay(addDays(startOfWeek(d, tz), 7, tz), tz).getTime() - 1)

export const startOfMonth = (d: Date, tz: AccountingTz): Date =>
  parseYmdInTz(`${formatYmdInTz(d, tz).slice(0, 7)}-01`, tz)

export const endOfMonth = (d: Date, tz: AccountingTz): Date =>
  new Date(
    startOfMonth(addMonths(startOfMonth(d, tz), 1, tz), tz).getTime() - 1,
  )

export const startOfQuarter = (d: Date, tz: AccountingTz): Date => {
  const { year, month } = orgParts(d, resolveAccountingTimezone(tz))
  const firstMonth = Math.floor((month - 1) / 3) * 3 + 1
  return parseYmdInTz(`${year}-${String(firstMonth).padStart(2, '0')}-01`, tz)
}

export const endOfQuarter = (d: Date, tz: AccountingTz): Date =>
  new Date(
    startOfQuarter(addMonths(startOfQuarter(d, tz), 3, tz), tz).getTime() - 1,
  )

export const startOfYear = (d: Date, tz: AccountingTz): Date =>
  parseYmdInTz(`${orgParts(d, resolveAccountingTimezone(tz)).year}-01-01`, tz)

export const endOfYear = (d: Date, tz: AccountingTz): Date =>
  new Date(startOfYear(addYears(startOfYear(d, tz), 1, tz), tz).getTime() - 1)

export const addDays = (d: Date, n: number, tz: AccountingTz): Date =>
  shiftOrgCalendar(d, resolveAccountingTimezone(tz), { months: 0, days: n })

export const addMonths = (d: Date, n: number, tz: AccountingTz): Date =>
  shiftOrgCalendar(d, resolveAccountingTimezone(tz), { months: n, days: 0 })

export const addYears = (d: Date, n: number, tz: AccountingTz): Date =>
  addMonths(d, n * 12, tz)

export const parseYmdInTz = (ymd: string, tz: AccountingTz): Date => {
  const [y, m, d] = ymd.split('-').map(Number)
  if (!y || !m || !d) return new Date(NaN)
  return orgInstant(
    { year: y, month: m, day: d, hour: 0, minute: 0, second: 0 },
    resolveAccountingTimezone(tz),
  )
}

export const formatYmdInTz = (d: Date, tz: AccountingTz): string => {
  const { year, month, day } = orgParts(d, resolveAccountingTimezone(tz))
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// react-datepicker hands us a local-midnight Date. Treat its Y-M-D triple as
// the user's intent and reinterpret as midnight in tz.
export const datePickerValueToTz = (picked: Date, tz: AccountingTz): Date =>
  parseYmdInTz(
    `${picked.getFullYear()}-${String(picked.getMonth() + 1).padStart(2, '0')}-${String(picked.getDate()).padStart(2, '0')}`,
    tz,
  )

// Inverse: produce a local-midnight Date the picker can render without shifting.
export const tzValueToDatePicker = (d: Date, tz: AccountingTz): Date => {
  const { year, month, day } = orgParts(d, resolveAccountingTimezone(tz))
  return new Date(year, month - 1, day)
}

export const sameDayInTz = (
  a: Date | undefined,
  b: Date | undefined,
  tz: AccountingTz,
): boolean => {
  if (!a && !b) return true
  if (!a || !b) return false
  return formatYmdInTz(a, tz) === formatYmdInTz(b, tz)
}
